(function exposeBenefitProgressCore(root, factory) {
  "use strict";
  var api = factory();

  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.BenefitProgressCore = api;
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function createBenefitProgressCore() {
  "use strict";

  var STATES = Object.freeze({
    EMPTY: "empty",
    PROGRESS: "progress",
    REACHED_PENDING: "reached_pending",
    REACHED_APPLIED: "reached_applied",
    LOST: "lost",
    ERROR: "error"
  });

  function finiteNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function positiveInteger(value, fallback) {
    return Math.max(0, Math.floor(finiteNumber(value, fallback)));
  }

  function normalizedText(value) {
    return String(value == null ? "" : value).trim().toLocaleLowerCase();
  }

  function identifier(value) {
    return String(value == null ? "" : value).trim();
  }

  function valueList(value) {
    if (Array.isArray(value)) {
      return value.map(identifier).filter(Boolean);
    }

    return String(value == null ? "" : value)
      .split(",")
      .map(function trimItem(item) {
        return item.trim();
      })
      .filter(Boolean);
  }

  function asSet(value) {
    return new Set(valueList(value));
  }

  function groupedValues(value) {
    if (!Array.isArray(value)) return [];
    return value
      .map(function normalizeGroup(group) {
        return valueList(group);
      })
      .filter(function keepGroup(group) {
        return group.length > 0;
      });
  }

  function isCampaignActive(config, now) {
    if (config && config.enabled === false) return false;

    var timestamp = finiteNumber(now, Date.now());
    var startsAt = config && config.startsAt ? Date.parse(config.startsAt) : NaN;
    var endsAt = config && config.endsAt ? Date.parse(config.endsAt) : NaN;

    if (Number.isFinite(startsAt) && timestamp < startsAt) return false;
    if (Number.isFinite(endsAt) && timestamp > endsAt) return false;
    return true;
  }

  function normalizeConfig(input) {
    var config = input || {};
    var progressMode = config.progressMode === "subtotal" ? "subtotal" : "quantity";
    var quantityModes = new Set(["units", "distinct_products", "distinct_variants"]);
    var quantityMode = quantityModes.has(config.quantityMode)
      ? config.quantityMode
      : "distinct_products";

    return {
      enabled: config.enabled !== false,
      campaignId: identifier(config.campaignId || "complete-seu-pacote"),
      campaignName: identifier(config.campaignName || "Complete seu pacote"),
      store: identifier(config.store),
      platform: identifier(config.platform),
      currency: identifier(config.currency || "USD").toUpperCase(),
      locale: identifier(config.locale || "en"),
      market: identifier(config.market),
      progressMode: progressMode,
      quantityMode: quantityMode,
      quantityTarget: Math.max(1, positiveInteger(config.quantityTarget, 2)),
      subtotalTargetMinor: Math.max(1, positiveInteger(config.subtotalTargetMinor, 10000)),
      rewardType: identifier(config.rewardType || "percentage"),
      rewardValue: Math.max(0, finiteNumber(config.rewardValue, 10)),
      discountTitle: identifier(config.discountTitle || "Complete seu pacote"),
      eligibleProductIds: valueList(config.eligibleProductIds),
      excludedProductIds: valueList(config.excludedProductIds),
      eligibleVariantIds: valueList(config.eligibleVariantIds),
      excludedVariantIds: valueList(config.excludedVariantIds),
      requiredProductGroups: groupedValues(config.requiredProductGroups),
      excludeFreeProducts: config.excludeFreeProducts !== false,
      startsAt: identifier(config.startsAt),
      endsAt: identifier(config.endsAt),
      analyticsEnabled: config.analyticsEnabled !== false
    };
  }

  function discountTitle(discount) {
    if (!discount || typeof discount !== "object") return "";
    return identifier(
      discount.title ||
        discount.name ||
        (discount.discount_application && discount.discount_application.title) ||
        (discount.discountApplication && discount.discountApplication.title)
    );
  }

  function discountAmount(discount) {
    if (!discount || typeof discount !== "object") return 0;
    return Math.max(
      0,
      finiteNumber(
        discount.amount != null
          ? discount.amount
          : discount.total_allocated_amount != null
            ? discount.total_allocated_amount
            : discount.totalAllocatedAmount != null
              ? discount.totalAllocatedAmount
              : discount.allocated_amount,
        0
      )
    );
  }

  function lineDiscounts(item) {
    return []
      .concat(item.line_level_discount_allocations || [])
      .concat(item.discount_allocations || [])
      .concat(item.discounts || []);
  }

  function normalizeLine(item, index, config) {
    var productId = identifier(
      item.product_id != null
        ? item.product_id
        : item.productId != null
          ? item.productId
          : item.product && item.product.id != null
            ? item.product.id
            : "line-product-" + index
    );
    var variantId = identifier(
      item.variant_id != null
        ? item.variant_id
        : item.variantId != null
          ? item.variantId
          : item.id != null
            ? item.id
            : "line-variant-" + index
    );
    var quantity = Math.max(1, positiveInteger(item.quantity, 1));
    var unitPrice = Math.max(
      0,
      finiteNumber(
        item.final_price != null
          ? item.final_price
          : item.finalPrice != null
            ? item.finalPrice
            : item.discounted_price != null
              ? item.discounted_price
              : item.price,
        0
      )
    );
    var finalLinePrice = Math.max(
      0,
      finiteNumber(
        item.final_line_price != null
          ? item.final_line_price
          : item.finalLinePrice != null
            ? item.finalLinePrice
            : item.line_price != null
              ? item.line_price
              : unitPrice * quantity,
        unitPrice * quantity
      )
    );

    var eligibleProducts = asSet(config.eligibleProductIds);
    var excludedProducts = asSet(config.excludedProductIds);
    var eligibleVariants = asSet(config.eligibleVariantIds);
    var excludedVariants = asSet(config.excludedVariantIds);
    var explicitlyEligible = item.eligible !== false && item.isEligible !== false;
    var productAllowed = eligibleProducts.size === 0 || eligibleProducts.has(productId);
    var variantAllowed = eligibleVariants.size === 0 || eligibleVariants.has(variantId);
    var excluded = excludedProducts.has(productId) || excludedVariants.has(variantId);
    var free = item.isFree === true || item.free === true || finalLinePrice <= 0;
    var eligible = explicitlyEligible && productAllowed && variantAllowed && !excluded;

    if (config.excludeFreeProducts && free) eligible = false;

    return {
      productId: productId,
      variantId: variantId,
      quantity: quantity,
      finalLinePrice: finalLinePrice,
      eligible: eligible,
      free: free,
      discounts: lineDiscounts(item)
    };
  }

  function normalizeCart(rawCart, inputConfig) {
    var config = normalizeConfig(inputConfig);
    var raw = rawCart || {};
    var rawItems = Array.isArray(raw.items)
      ? raw.items
      : Array.isArray(raw.lines)
        ? raw.lines
        : [];
    var items = rawItems.map(function mapLine(item, index) {
      return normalizeLine(item || {}, index, config);
    });
    var eligibleItems = items.filter(function keepEligible(item) {
      return item.eligible;
    });
    var eligibleSubtotal = eligibleItems.reduce(function sumSubtotal(total, item) {
      return total + item.finalLinePrice;
    }, 0);
    var itemCount = items.reduce(function sumQuantity(total, item) {
      return total + item.quantity;
    }, 0);
    var eligibleUnits = eligibleItems.reduce(function sumEligibleQuantity(total, item) {
      return total + item.quantity;
    }, 0);
    var productIds = Array.from(
      new Set(
        eligibleItems.map(function getProduct(item) {
          return item.productId;
        })
      )
    );
    var variantIds = Array.from(
      new Set(
        eligibleItems.map(function getVariant(item) {
          return item.variantId;
        })
      )
    );
    var cartDiscounts = []
      .concat(raw.cart_level_discount_applications || [])
      .concat(raw.discount_applications || [])
      .concat(raw.discounts || []);
    var allDiscounts = cartDiscounts.concat(
      eligibleItems.reduce(function collectLineDiscounts(discounts, item) {
        return discounts.concat(item.discounts);
      }, [])
    );
    var expectedTitle = normalizedText(config.discountTitle);
    var matchingDiscount = allDiscounts.find(function matchesConfiguredDiscount(discount) {
      return expectedTitle && normalizedText(discountTitle(discount)) === expectedTitle;
    });
    var platformRewardAmount = Math.max(0, finiteNumber(raw.platformRewardAmount, 0));
    var confirmedByPlatform = raw.platformRewardConfirmed === true && (
      config.rewardType === "service" || platformRewardAmount > 0
    );
    var discountConfirmed = confirmedByPlatform || Boolean(
      matchingDiscount && discountAmount(matchingDiscount) > 0
    );
    var platformSubtotal = finiteNumber(
      raw.items_subtotal_price != null
        ? raw.items_subtotal_price
        : raw.itemsSubtotalPrice != null
          ? raw.itemsSubtotalPrice
          : raw.total_price != null
            ? raw.total_price
            : raw.totalPrice,
      items.reduce(function sumAll(total, item) {
        return total + item.finalLinePrice;
      }, 0)
    );

    return {
      raw: raw,
      items: items,
      eligibleItems: eligibleItems,
      itemCount: itemCount,
      eligibleUnits: eligibleUnits,
      distinctProducts: productIds.length,
      distinctVariants: variantIds.length,
      eligibleSubtotal: Math.max(0, finiteNumber(raw.eligibleSubtotal, eligibleSubtotal)),
      cartSubtotal: Math.max(0, platformSubtotal),
      currency: identifier(raw.currency || config.currency).toUpperCase(),
      productIds: productIds,
      variantIds: variantIds,
      discountConfirmed: discountConfirmed,
      discountAmount: confirmedByPlatform
        ? platformRewardAmount
        : discountAmount(matchingDiscount)
    };
  }

  function summarizeCart(rawCart, inputConfig) {
    var config = normalizeConfig(inputConfig);
    var cart = normalizeCart(rawCart, config);
    var requiredGroupProgress = config.requiredProductGroups.length > 0
      ? config.requiredProductGroups.reduce(function countSatisfiedGroups(total, group) {
          var groupSet = new Set(group);
          return total + (cart.productIds.some(function productInGroup(productId) {
            return groupSet.has(productId);
          }) ? 1 : 0);
        }, 0)
      : null;
    var quantityCurrent = requiredGroupProgress != null
      ? requiredGroupProgress
      : config.quantityMode === "units"
        ? cart.eligibleUnits
        : config.quantityMode === "distinct_variants"
          ? cart.distinctVariants
          : cart.distinctProducts;
    var current = config.progressMode === "subtotal"
      ? cart.eligibleSubtotal
      : quantityCurrent;
    var target = config.progressMode === "subtotal"
      ? config.subtotalTargetMinor
      : requiredGroupProgress != null
        ? config.requiredProductGroups.length
        : config.quantityTarget;
    var remaining = Math.max(0, target - current);
    var progressPercentage = target > 0
      ? Math.max(0, Math.min(100, Math.round((current / target) * 100)))
      : 0;
    var reached = target > 0 && current >= target;
    var state = current <= 0
      ? STATES.EMPTY
      : reached
        ? cart.discountConfirmed
          ? STATES.REACHED_APPLIED
          : STATES.REACHED_PENDING
        : STATES.PROGRESS;

    return {
      active: isCampaignActive(config),
      state: state,
      reached: reached,
      discountConfirmed: cart.discountConfirmed,
      discountAmount: cart.discountAmount,
      current: current,
      target: target,
      remaining: remaining,
      progressPercentage: progressPercentage,
      progressMode: config.progressMode,
      quantityMode: config.quantityMode,
      itemCount: cart.itemCount,
      eligibleUnits: cart.eligibleUnits,
      distinctProducts: cart.distinctProducts,
      distinctVariants: cart.distinctVariants,
      cartSubtotal: cart.cartSubtotal,
      eligibleSubtotal: cart.eligibleSubtotal,
      currency: cart.currency,
      productIds: cart.productIds,
      variantIds: cart.variantIds,
      signature: [
        state,
        current,
        target,
        cart.itemCount,
        cart.eligibleSubtotal,
        cart.discountConfirmed ? 1 : 0
      ].join(":"),
      config: config
    };
  }

  function transitionState(previous, current) {
    if (!previous || !current) return current ? current.state : STATES.EMPTY;
    if (previous.reached && !current.reached) return STATES.LOST;
    return current.state;
  }

  function formatMoneyMinor(amount, currency, locale) {
    var safeCurrency = identifier(currency || "USD").toUpperCase();
    var safeLocale = identifier(locale || "en");
    var majorValue = Math.max(0, finiteNumber(amount, 0)) / 100;

    try {
      return new Intl.NumberFormat(safeLocale, {
        style: "currency",
        currency: safeCurrency
      }).format(majorValue);
    } catch (_error) {
      return safeCurrency + " " + majorValue.toFixed(2);
    }
  }

  function replaceTokens(template, values) {
    return Object.keys(values).reduce(function replaceValue(text, key) {
      return text.split("__" + key + "__").join(String(values[key]));
    }, identifier(template));
  }

  function messageFor(summary, copy, displayState) {
    var state = displayState || summary.state;
    var strings = copy || {};
    var remainingValue = summary.progressMode === "subtotal"
      ? formatMoneyMinor(summary.remaining, summary.currency, summary.config.locale)
      : summary.remaining;
    var values = {
      remaining: remainingValue,
      current: summary.current,
      target: summary.target,
      percentage: summary.progressPercentage,
      reward: summary.config.rewardValue,
      campaign: summary.config.campaignName
    };
    var titleKey = state === STATES.REACHED_APPLIED
      ? "titleApplied"
      : state === STATES.REACHED_PENDING
        ? "titlePending"
        : state === STATES.LOST
          ? "titleLost"
          : state === STATES.ERROR
            ? "titleError"
            : "titleProgress";
    var messageKey = state === STATES.EMPTY
      ? "empty"
      : state === STATES.REACHED_APPLIED
        ? "applied"
        : state === STATES.REACHED_PENDING
          ? "pending"
          : state === STATES.LOST
            ? summary.remaining === 1 ? "lostOne" : "lostMany"
            : state === STATES.ERROR
              ? "error"
              : summary.remaining === 1 ? "progressOne" : "progressMany";

    return {
      title: replaceTokens(strings[titleKey] || summary.config.campaignName, values),
      message: replaceTokens(strings[messageKey] || "", values)
    };
  }

  function analyticsPayload(eventName, summary, inputConfig, sourceLocation, experimentVariant) {
    var config = normalizeConfig(inputConfig);
    return {
      event: identifier(eventName),
      campaign_id: config.campaignId,
      campaign_name: config.campaignName,
      store: config.store,
      platform: config.platform,
      currency: summary.currency,
      language: config.locale,
      market: config.market,
      progress_mode: summary.progressMode,
      current_value: summary.current,
      target_value: summary.target,
      remaining_value: summary.remaining,
      progress_percentage: summary.progressPercentage,
      reward_type: config.rewardType,
      reward_value: config.rewardValue,
      cart_quantity: summary.itemCount,
      cart_subtotal: summary.cartSubtotal,
      eligible_subtotal: summary.eligibleSubtotal,
      source_location: identifier(sourceLocation),
      experiment_variant: identifier(experimentVariant || "off"),
      discount_confirmed: summary.discountConfirmed
    };
  }

  return Object.freeze({
    STATES: STATES,
    analyticsPayload: analyticsPayload,
    formatMoneyMinor: formatMoneyMinor,
    isCampaignActive: isCampaignActive,
    messageFor: messageFor,
    normalizeCart: normalizeCart,
    normalizeConfig: normalizeConfig,
    summarizeCart: summarizeCart,
    transitionState: transitionState
  });
});

(function initializeShopifyBenefitProgress(window, document) {
  "use strict";

  if (window.__shopifyBenefitProgressV1) return;
  window.__shopifyBenefitProgressV1 = true;

  var core = window.BenefitProgressCore;
  if (!core || !document.querySelector("[data-benefit-progress]")) return;

  var state = {
    abortController: null,
    sequence: 0,
    lastSummary: null,
    lastConfig: null,
    lastVariant: "off",
    refreshTimer: null,
    sentEvents: new Set(),
    observedContainers: new WeakSet(),
    observer: null,
    recommendationLocks: new Set()
  };

  function booleanValue(value, fallback) {
    if (value === "true" || value === true) return true;
    if (value === "false" || value === false) return false;
    return fallback;
  }

  function numberValue(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll("[data-benefit-progress]"));
  }

  function directChild(container, descendant) {
    var child = descendant;
    while (child && child.parentElement !== container) child = child.parentElement;
    return child && child.parentElement === container ? child : null;
  }

  function positionRoots() {
    document.querySelectorAll("#eg-super-cart-drawer-goal").forEach(function hideLegacyGoal(goal) {
      goal.hidden = true;
      goal.setAttribute("aria-hidden", "true");
      goal.style.display = "none";
    });

    roots().forEach(function positionRoot(root) {
      var surface = root.dataset.surface;

      if (surface === "product") {
        var productInfo = root.closest("product-info");
        var buyButtons = productInfo && productInfo.querySelector(".buy-button-wrapper");
        var productAnchor = productInfo && buyButtons && directChild(productInfo, buyButtons);
        if (productInfo && productAnchor && root.nextElementSibling !== productAnchor) {
          productInfo.insertBefore(root, productAnchor);
        }
        return;
      }

      if (surface === "drawer") {
        var drawerInner = document.querySelector("cart-drawer .drawer__inner");
        var drawerItems = drawerInner && drawerInner.querySelector("cart-drawer-items");
        var drawerFooter = drawerInner && drawerInner.querySelector(".drawer__footer");
        var drawerAnchor = drawerItems || drawerFooter;
        if (drawerInner && drawerAnchor && (root.parentElement !== drawerInner || root.nextElementSibling !== drawerAnchor)) {
          drawerInner.insertBefore(root, drawerAnchor);
        }
        return;
      }

      if (surface === "cart_page") {
        var cartFooter = document.querySelector("#main-cart-footer .cart__footer, #main-cart-footer");
        var checkoutArea = cartFooter && cartFooter.querySelector(".cart__ctas, [name='checkout']");
        var cartAnchor = cartFooter && checkoutArea && directChild(cartFooter, checkoutArea);
        if (cartFooter && cartAnchor && (root.parentElement !== cartFooter || root.nextElementSibling !== cartAnchor)) {
          cartFooter.insertBefore(root, cartAnchor);
        }
      }
    });
  }

  function normalizedHost(value) {
    return String(value || "").trim().toLocaleLowerCase().replace(/^www\./, "");
  }

  function hideAll(reason) {
    roots().forEach(function hideRoot(root) {
      root.hidden = true;
      root.dataset.state = reason;
    });
  }

  function identityMatches(root, currency) {
    var expectedDomain = normalizedHost(root.dataset.expectedDomain);
    var expectedCurrency = String(root.dataset.expectedCurrency || "").trim().toUpperCase();
    var domainMatches = !expectedDomain || expectedDomain === normalizedHost(window.location.hostname);
    var currencyMatches = !expectedCurrency || !currency || expectedCurrency === String(currency).toUpperCase();
    return domainMatches && currencyMatches;
  }

  function copyFrom(root) {
    return {
      titleProgress: root.dataset.copyTitleProgress,
      titlePending: root.dataset.copyTitlePending,
      titleApplied: root.dataset.copyTitleApplied,
      titleLost: root.dataset.copyTitleLost,
      titleError: root.dataset.copyTitleError,
      empty: root.dataset.copyEmpty,
      progressOne: root.dataset.copyProgressOne,
      progressMany: root.dataset.copyProgressMany,
      pending: root.dataset.copyPending,
      applied: root.dataset.copyApplied,
      lostOne: root.dataset.copyLostOne,
      lostMany: root.dataset.copyLostMany,
      error: root.dataset.copyError
    };
  }

  function configFrom(root) {
    return core.normalizeConfig({
      enabled: booleanValue(root.dataset.enabled, false),
      campaignId: root.dataset.campaignId,
      campaignName: root.dataset.campaignName,
      store: root.dataset.store,
      platform: "shopify",
      currency: root.dataset.currency,
      locale: root.dataset.locale || document.documentElement.lang,
      market: root.dataset.market,
      progressMode: root.dataset.progressMode,
      quantityMode: root.dataset.quantityMode,
      quantityTarget: numberValue(root.dataset.quantityTarget, 2),
      subtotalTargetMinor: numberValue(root.dataset.subtotalTargetMinor, 10000),
      rewardType: root.dataset.rewardType,
      rewardValue: numberValue(root.dataset.rewardValue, 10),
      discountTitle: root.dataset.discountTitle,
      eligibleProductIds: root.dataset.eligibleProductIds,
      excludedProductIds: root.dataset.excludedProductIds,
      eligibleVariantIds: root.dataset.eligibleVariantIds,
      excludedVariantIds: root.dataset.excludedVariantIds,
      excludeFreeProducts: booleanValue(root.dataset.excludeFreeProducts, true),
      startsAt: root.dataset.startsAt,
      endsAt: root.dataset.endsAt,
      analyticsEnabled: booleanValue(root.dataset.analyticsEnabled, true)
    });
  }

  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      return Boolean(
        privacy &&
          typeof privacy.analyticsProcessingAllowed === "function" &&
          privacy.analyticsProcessingAllowed()
      );
    } catch (_error) {
      return false;
    }
  }

  function storageGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  function storageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch (_error) {
      return;
    }
  }

  function randomPercentage() {
    if (window.crypto && typeof window.crypto.getRandomValues === "function") {
      var value = new Uint32Array(1);
      window.crypto.getRandomValues(value);
      return (value[0] / 4294967296) * 100;
    }
    return Math.random() * 100;
  }

  function experimentVariant(root, config) {
    var mode = root.dataset.experimentMode || "off";
    if (mode === "control") return "control";
    if (mode === "quantity") return "quantity";
    if (mode === "subtotal") return "subtotal";
    if (mode !== "auto") return config.progressMode;
    if (!analyticsAllowed()) return "control";

    var key = "benefit-progress:" + config.campaignId + ":variant";
    var stored = storageGet(window.localStorage, key);
    if (["control", "quantity", "subtotal"].includes(stored)) return stored;

    var controlPercent = Math.max(0, Math.min(100, numberValue(root.dataset.experimentControlPercent, 34)));
    var subtotalPercent = Math.max(0, Math.min(100 - controlPercent, numberValue(root.dataset.experimentSubtotalPercent, 33)));
    var draw = randomPercentage();
    var assigned = draw < controlPercent
      ? "control"
      : draw < controlPercent + subtotalPercent
        ? "subtotal"
        : "quantity";
    storageSet(window.localStorage, key, assigned);
    return assigned;
  }

  function configForVariant(config, variant) {
    if (variant === "quantity" || variant === "subtotal") {
      return core.normalizeConfig(Object.assign({}, config, { progressMode: variant }));
    }
    return config;
  }

  function emitEvent(name, summary, config, root, variant, dedupeSuffix) {
    if (!config.analyticsEnabled) return;
    var surface = root ? root.dataset.surface || "unknown" : "unknown";
    var key = [name, surface, dedupeSuffix || summary.signature].join(":");
    if (state.sentEvents.has(key)) return;
    state.sentEvents.add(key);

    var payload = core.analyticsPayload(name, summary, config, surface, variant);
    document.dispatchEvent(new CustomEvent(name, { detail: payload }));

    if (analyticsAllowed() && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }
  }

  function visibleRecommendations(root, summary) {
    var region = root.querySelector("[data-benefit-recommendations]");
    if (!region) return;
    var shouldShow = summary.state === core.STATES.PROGRESS && summary.current > 0;
    var visibleCount = 0;

    region.querySelectorAll("[data-benefit-recommendation]").forEach(function filterRecommendation(item) {
      var productId = String(item.dataset.productId || "");
      var hidden = !shouldShow || summary.productIds.includes(productId);
      item.hidden = hidden;
      if (!hidden) visibleCount += 1;
    });

    region.hidden = visibleCount === 0;
    if (visibleCount > 0) {
      emitEvent(
        "benefit_recommendation_view",
        summary,
        summary.config,
        root,
        state.lastVariant,
        summary.signature
      );
    }
  }

  function renderRoot(root, summary, displayState, variant) {
    if (!summary.active || variant === "control") {
      root.hidden = true;
      return;
    }

    root.hidden = false;
    root.dataset.state = displayState;
    var copy = copyFrom(root);
    var text = core.messageFor(summary, copy, displayState);
    var title = root.querySelector("[data-benefit-title]");
    var message = root.querySelector("[data-benefit-message]");
    var icon = root.querySelector("[data-benefit-icon]");
    var visual = root.querySelector("[data-benefit-visual]");
    var progressbar = root.querySelector("[data-benefit-progressbar]");
    var fill = root.querySelector("[data-benefit-fill]");
    var metaCurrent = root.querySelector("[data-benefit-current]");
    var metaTarget = root.querySelector("[data-benefit-target]");

    if (title) title.textContent = text.title;
    if (message) message.textContent = text.message;
    if (icon) {
      icon.textContent = displayState === core.STATES.REACHED_APPLIED
        ? "✓"
        : displayState === core.STATES.LOST || displayState === core.STATES.ERROR
          ? "!"
          : "🎁";
    }

    var hideProgress = displayState === core.STATES.EMPTY || displayState === core.STATES.ERROR;
    if (visual) visual.hidden = hideProgress;

    if (!hideProgress && progressbar) {
      var currentForAria = Math.min(summary.current, summary.target);
      progressbar.setAttribute("aria-valuemin", "0");
      progressbar.setAttribute("aria-valuemax", String(summary.target));
      progressbar.setAttribute("aria-valuenow", String(currentForAria));
      progressbar.setAttribute("aria-valuetext", text.message);
    }

    if (fill) fill.style.inlineSize = summary.progressPercentage + "%";

    if (metaCurrent) {
      metaCurrent.textContent = summary.progressMode === "subtotal"
        ? core.formatMoneyMinor(summary.current, summary.currency, summary.config.locale)
        : String(summary.current);
    }
    if (metaTarget) {
      metaTarget.textContent = summary.progressMode === "subtotal"
        ? core.formatMoneyMinor(summary.target, summary.currency, summary.config.locale)
        : String(summary.target);
    }

    visibleRecommendations(root, summary);
  }

  function renderAll(summary, previous, variant) {
    var displayState = core.transitionState(previous, summary);
    roots().forEach(function renderSingle(root) {
      renderRoot(root, summary, displayState, variant);
    });
  }

  function cartUrl(pathname) {
    var localeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : "/";
    if (!localeRoot.endsWith("/")) localeRoot += "/";
    return localeRoot + pathname.replace(/^\//, "");
  }

  function eventRoots(summary, config, variant, previous) {
    roots().forEach(function registerEvents(root) {
      emitEvent("benefit_progress_view", summary, config, root, variant, "session");
      if (summary.current > 0 && (!previous || previous.current <= 0)) {
        emitEvent("benefit_progress_started", summary, config, root, variant);
      }
      if (!previous || previous.signature !== summary.signature) {
        emitEvent("benefit_progress_updated", summary, config, root, variant);
      }
      if (summary.reached && (!previous || !previous.reached)) {
        emitEvent("benefit_milestone_reached", summary, config, root, variant);
      }
      if (summary.discountConfirmed && (!previous || !previous.discountConfirmed)) {
        emitEvent("benefit_reward_unlocked", summary, config, root, variant);
        emitEvent("benefit_reward_applied", summary, config, root, variant);
      }
      if (previous && previous.discountConfirmed && !summary.discountConfirmed) {
        emitEvent("benefit_reward_removed", summary, config, root, variant);
      }
    });
  }

  async function refresh() {
    positionRoots();
    var firstRoot = roots()[0];
    if (!firstRoot) return;
    var initialConfig = configFrom(firstRoot);
    var variant = experimentVariant(firstRoot, initialConfig);
    var config = configForVariant(initialConfig, variant);
    state.lastConfig = config;
    state.lastVariant = variant;

    if (!identityMatches(firstRoot, config.currency)) {
      hideAll("identity_mismatch");
      return;
    }

    if (!core.isCampaignActive(config) || variant === "control") {
      hideAll(variant === "control" ? "control" : "inactive");
      return;
    }

    if (state.abortController) state.abortController.abort();
    state.abortController = typeof AbortController === "function" ? new AbortController() : null;
    var requestSequence = ++state.sequence;

    try {
      var response = await window.fetch(cartUrl("cart.js"), {
        method: "GET",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        cache: "no-store",
        signal: state.abortController ? state.abortController.signal : undefined
      });
      if (!response.ok) throw new Error("cart_unavailable");
      var cart = await response.json();
      if (requestSequence !== state.sequence) return;

      var summary = core.summarizeCart(cart, config);
      if (!identityMatches(firstRoot, summary.currency)) {
        hideAll("identity_mismatch");
        return;
      }
      var previous = state.lastSummary;
      renderAll(summary, previous, variant);
      eventRoots(summary, config, variant, previous);
      state.lastSummary = summary;
      bindMutationObserver();
    } catch (error) {
      if (error && error.name === "AbortError") return;
      var fallback = state.lastSummary || core.summarizeCart({ items: [], currency: config.currency }, config);
      roots().forEach(function renderError(root) {
        renderRoot(root, fallback, core.STATES.ERROR, variant);
        emitEvent("benefit_reward_failed", fallback, config, root, variant, "cart-read");
      });
    }
  }

  function scheduleRefresh() {
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = window.setTimeout(refresh, 180);
  }

  function relevantMutation(mutation) {
    var target = mutation.target && mutation.target.nodeType === 1
      ? mutation.target
      : mutation.target && mutation.target.parentElement;
    return !(target && target.closest && target.closest("[data-benefit-progress]"));
  }

  function bindMutationObserver() {
    if (!state.observer && typeof MutationObserver === "function") {
      state.observer = new MutationObserver(function onCartMutation(mutations) {
        if (mutations.some(relevantMutation)) scheduleRefresh();
      });
    }
    if (!state.observer) return;

    document
      .querySelectorAll("cart-drawer, cart-notification, main-cart-items, #main-cart-footer, .cart-count-bubble")
      .forEach(function observeContainer(container) {
        if (state.observedContainers.has(container)) return;
        state.observedContainers.add(container);
        state.observer.observe(container, { childList: true, subtree: true, attributes: true });
      });
  }

  async function addRecommendation(button) {
    var variantId = String(button.dataset.variantId || "");
    if (!variantId || state.recommendationLocks.has(variantId)) return;
    state.recommendationLocks.add(variantId);
    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    try {
      var cartSurface = document.querySelector("cart-drawer, cart-notification");
      var sections = cartSurface && typeof cartSurface.getSectionsToRender === "function"
        ? cartSurface.getSectionsToRender().map(function sectionId(section) {
            return section.section;
          })
        : [];
      var response = await window.fetch(cartUrl("cart/add.js"), {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sections,
          sections_url: window.location.pathname
        })
      });
      var data = await response.json();
      if (!response.ok || data.status) throw new Error("recommendation_add_failed");

      if (cartSurface && typeof cartSurface.renderContents === "function" && data.sections) {
        cartSurface.renderContents(data);
      }
      if (typeof window.publish === "function" && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: "benefit-progress",
          cartData: data
        });
      }
      document.dispatchEvent(new CustomEvent("benefit-progress:refresh"));

      if (state.lastSummary && state.lastConfig) {
        var root = button.closest("[data-benefit-progress]");
        emitEvent(
          "benefit_recommended_product_added",
          state.lastSummary,
          state.lastConfig,
          root,
          state.lastVariant,
          variantId + ":" + state.lastSummary.signature
        );
      }
      scheduleRefresh();
    } catch (_error) {
      if (state.lastSummary && state.lastConfig) {
        emitEvent(
          "benefit_reward_failed",
          state.lastSummary,
          state.lastConfig,
          button.closest("[data-benefit-progress]"),
          state.lastVariant,
          "recommendation:" + variantId
        );
      }
    } finally {
      state.recommendationLocks.delete(variantId);
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  }

  document.addEventListener("click", function onDocumentClick(event) {
    var recommendation = event.target.closest && event.target.closest("[data-benefit-add]");
    var recommendationLink = event.target.closest && event.target.closest("[data-benefit-recommendation-link]");
    var checkout = event.target.closest && event.target.closest("#checkout, #CartDrawer-Checkout, [name='checkout']");

    if (recommendation) {
      event.preventDefault();
      addRecommendation(recommendation);
    }

    if (recommendationLink && state.lastSummary && state.lastConfig) {
      emitEvent(
        "benefit_recommendation_clicked",
        state.lastSummary,
        state.lastConfig,
        recommendationLink.closest("[data-benefit-progress]"),
        state.lastVariant,
        recommendationLink.getAttribute("href") + ":" + state.lastSummary.signature
      );
    }

    if (checkout && state.lastSummary && state.lastConfig) {
      emitEvent(
        "benefit_checkout_started",
        state.lastSummary,
        state.lastConfig,
        checkout.closest("[data-benefit-progress]") || roots()[0],
        state.lastVariant,
        state.lastSummary.signature
      );
    }
  }, true);

  ["cart:updated", "cart:refresh", "product:added", "benefit-progress:refresh", "shopify:section:load"]
    .forEach(function bindRefreshEvent(eventName) {
      document.addEventListener(eventName, scheduleRefresh);
    });

  window.addEventListener("pageshow", scheduleRefresh);
  window.addEventListener("pagehide", function abortPendingRequest() {
    if (state.abortController) state.abortController.abort();
  });

  if (typeof window.subscribe === "function" && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, scheduleRefresh);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function onReady() {
      bindMutationObserver();
      refresh();
    }, { once: true });
  } else {
    bindMutationObserver();
    refresh();
  }
})(window, document);
